import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppAPIService } from "../../../../services/AppAPIService";
import {
  isFinancialAdminPreviewUser,
  isFinancialDashboardEnabled,
} from "../../../../utils/FeatureFlags";
import InputForm from "../../../../components/InputForm";
import InputDateForm from "../../../../components/InputDateForm";
import {
  formatFinancialMoney,
  financialDateForPicker,
  financialDateFromPicker,
} from "./financialPresentation";
import withFinancialSession from "./withFinancialSession";
import {
  deliverySources,
  deliveryStates,
  deliveryReasonLabel,
  deliveryStageLabel,
  validDeliveryContext,
  deliveryInstallmentLabel,
  validDeliveryMonitor,
  deliveryMonitorCsv,
} from "./deliveryMonitorContract";
import styles from "./collections.module.scss";
const tabs = {
  queue: ["Fila de envios", "Trabalhos registrados"],
  history: ["Envios processados", "Aceitação e confirmações"],
  attention: ["Atenção e bloqueios", "Falhas, retenções e cancelamentos"],
  all: ["Todos os registros", "Todos os fluxos"],
};
const initial = {
  view: "queue",
  source: "",
  state: "",
  paymentId: "",
  dateFrom: "",
  dateTo: "",
};
const date = (v) =>
  v
    ? new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "America/Sao_Paulo",
      }).format(new Date(v))
    : "Não registrado";
const day = (v) => (v ? v.split("-").reverse().join("/") : "Não registrada");
function FinancialDeliveryMonitorPage() {
  if (window.setPageTitle) window.setPageTitle("Envios financeiros");
  const user = useSelector((s) => s.auth.user),
    allowed =
      isFinancialDashboardEnabled() && isFinancialAdminPreviewUser(user),
    api = useMemo(() => AppAPIService.getInstance(), []);
  const [filters, setFilters] = useState(initial),
    [applied, setApplied] = useState(initial),
    [page, setPage] = useState(1),
    [revision, setRevision] = useState(0),
    [request, setRequest] = useState({ loading: false, data: null, error: "" });
  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setRequest({ loading: true, data: null, error: "" });
    const params = new URLSearchParams({ page: String(page) });
    Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
    api
      .makeHttpRequest({
        method: "GET",
        url: `/financial/deliveries?${params}`,
        sensitive: true,
      })
      .then(async (data) => {
        if (!validDeliveryMonitor(data, applied, page))
          throw Error("INVALID_DELIVERY_MONITOR");
        if (!active) return;
        const initialData = {
          ...data,
          items: data.items.map((row) => ({
            ...row,
            billingStatus: row.paymentId ? "LOADING" : "UNAVAILABLE",
            billingContext: null,
          })),
        };
        setRequest({ loading: false, data: initialData, error: "" });
        // Progressive, sequential GETs keep the ledger usable and respect the provider queue.
        const contexts = new Map();
        for (const row of initialData.items) {
          if (!active || Date.parse(data.expiresAt) <= Date.now()) break;
          if (!row.paymentId) continue;
          let context = null;
          try {
            const cacheKey = row.paymentId + ":" + (row.customerId || "");
            if (contexts.has(cacheKey)) {
              context = {
                ...contexts.get(cacheKey),
                recordSource: row.source,
                recordId: row.id,
              };
            } else {
              context = await api.makeHttpRequest({
                method: "GET",
                url: `/financial/deliveries/${encodeURIComponent(row.source)}/${encodeURIComponent(row.id)}/context`,
                sensitive: true,
              });
            }
            if (!validDeliveryContext(context, row))
              throw Error("INVALID_BILLING_CONTEXT");
            contexts.set(cacheKey, context);
          } catch {
            context = null;
          }
          if (!active) break;
          setRequest((previous) => {
            if (
              !previous.data ||
              Date.parse(previous.data.expiresAt) <= Date.now()
            )
              return previous;
            return {
              ...previous,
              data: {
                ...previous.data,
                expiresAt:
                  context &&
                  Date.parse(context.expiresAt) <
                    Date.parse(previous.data.expiresAt)
                    ? context.expiresAt
                    : previous.data.expiresAt,
                items: previous.data.items.map((item) =>
                  item.source === row.source && item.id === row.id
                    ? {
                        ...item,
                        billingContext: context,
                        billingStatus: context ? "AVAILABLE" : "UNAVAILABLE",
                      }
                    : item,
                ),
              },
            };
          });
        }
      })
      .catch(
        (error) =>
          active &&
          setRequest({
            loading: false,
            data: null,
            error:
              error?.response?.status === 403
                ? "Seu usuário não tem permissão para consultar os envios."
                : "Não foi possível consultar os registros de envio. Isso não indica fila vazia. Atualize para tentar novamente.",
          }),
      );
    return () => {
      active = false;
    };
  }, [allowed, api, applied, page, revision]);
  useEffect(() => {
    if (!request.data) return undefined;
    const timer = setTimeout(
      () =>
        setRequest({
          loading: false,
          data: null,
          error:
            "A consulta expirou. Atualize antes de usar ou exportar os registros.",
        }),
      Math.max(0, Date.parse(request.data.expiresAt) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [request.data]);
  if (!allowed)
    return (
      <p role="status">Envios financeiros indisponíveis para este acesso.</p>
    );
  const data = request.data,
    dirty = JSON.stringify(filters) !== JSON.stringify(applied);
  const changeView = (view) => {
    const f = { ...applied, view, state: "" };
    setFilters(f);
    setApplied(f);
    setPage(1);
  };
  const apply = (e) => {
    e.preventDefault();
    setApplied({ ...filters });
    setPage(1);
    setRevision((r) => r + 1);
  };
  const exportPage = () => {
    if (!validDeliveryMonitor(data, applied, page)) return;
    const url = URL.createObjectURL(
        new Blob([deliveryMonitorCsv(data)], {
          type: "text/csv;charset=utf-8",
        }),
      ),
      link = document.createElement("a");
    link.href = url;
    link.download = `envios-financeiros-pagina-${page}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const select = (key, label, options) => (
    <InputForm
      name={"delivery-" + key}
      label={label}
      isSelect
      options={[
        { value: "", label: "Todos" },
        ...Object.entries(options).map(([value, label]) => ({ value, label })),
      ]}
      value={{ value: filters[key], label: options[filters[key]] || "Todos" }}
      isClearable={false}
      onChange={(option) =>
        setFilters({ ...filters, [key]: option?.value || "" })
      }
    />
  );
  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Link to="/admin/financeiro/cobrancas">Consultar cobranças</Link>
        <div className={styles.monitorActions}>
          <button
            type="button"
            onClick={() => setRevision((r) => r + 1)}
            disabled={request.loading}
          >
            Atualizar registros
          </button>
          <button
            type="button"
            disabled={!data || request.loading}
            onClick={exportPage}
          >
            Exportar página CSV
          </button>
        </div>
      </div>
      <p className={styles.sourceNote}>
        Registros reais das filas do portal. A aprovação de uma planilha não
        agenda mensagens: o trabalho precisa estar registrado no robô. Esta tela
        apenas consulta.
      </p>
      <nav
        className={`${styles.tabs} ${styles.monitorTabs}`}
        aria-label="Acompanhamento de envios"
      >
        {Object.entries(tabs).map(([key, [title, subtitle]]) => (
          <button
            key={key}
            type="button"
            aria-pressed={applied.view === key}
            className={applied.view === key ? styles.activeTab : ""}
            onClick={() => changeView(key)}
          >
            <strong>{title}</strong>
            <span>{subtitle}</span>
          </button>
        ))}
      </nav>
      <form
        className="xv-inline-filter-row xv-financial-filters xv-financial-filters--channel"
        onSubmit={apply}
      >
        {select("source", "Fluxo", deliverySources)}
        {select("state", "Situação", deliveryStates)}
        <InputForm
          name="delivery-payment"
          label="ID da cobrança"
          withoutTextTransform
          value={filters.paymentId}
          onChange={(value) => setFilters({ ...filters, paymentId: value })}
          placeholder="pay_…"
        />
        <InputDateForm
          name="delivery-from"
          label="De"
          selectedDate={financialDateForPicker(filters.dateFrom)}
          onChange={(value) =>
            setFilters({ ...filters, dateFrom: financialDateFromPicker(value) })
          }
        />
        <InputDateForm
          name="delivery-to"
          label="Até"
          selectedDate={financialDateForPicker(filters.dateTo)}
          onChange={(value) =>
            setFilters({ ...filters, dateTo: financialDateFromPicker(value) })
          }
        />
        <button
          className="btn btn-primary"
          type="submit"
          disabled={request.loading}
        >
          Aplicar filtros
        </button>
      </form>
      {dirty && (
        <p role="status" className={styles.sourceNote}>
          Há filtros alterados. Aplique para atualizar os resultados.
        </p>
      )}
      {request.loading && (
        <p role="status" className={styles.feedback}>
          Consultando filas e histórico…
        </p>
      )}
      {request.error && (
        <p role="alert" className={styles.feedback}>
          {request.error}
        </p>
      )}
      {data && (
        <>
          <section
            className={styles.summary}
            aria-label="Resumo dos filtros aplicados"
          >
            {Object.entries(tabs).map(([key, [label]]) => (
              <div key={key}>
                <span>{label}</span>
                <strong>{data.summary[key]}</strong>
                <small>Registros, não clientes únicos</small>
              </div>
            ))}
          </section>
          <p className={styles.sourceNote}>
            Consulta de {date(data.generatedAt)} (Brasília). Período dos
            filtros: data de registro em UTC. Configuração não comprova saúde do
            robô.
          </p>
          <p className={styles.sourceNote}>
            Aceito pelo provedor significa que o servidor aceitou a mensagem;
            não confirma chegada à caixa de entrada. Destinatários não retidos
            nos registros antigos não são reconstruídos pelo cadastro atual.
          </p>
          <details className={styles.monitorDetails}>
            <summary>Configuração observada nesta instância</summary>
            <ul>
              {Object.entries({
                existingBoletos: "Boletos existentes",
                automaticBoletos: "Novos boletos",
                recurringEmail: "E-mail recorrente",
                voiceBoletos: "Boleto solicitado à Donna",
              }).map(([k, label]) => (
                <li key={k}>
                  {label}:{" "}
                  {data.configuration?.[k] === true
                    ? "habilitado na configuração"
                    : "desligado na configuração"}
                </li>
              ))}
            </ul>
            <p>Não confirma execução de agendadores nem habilita envios.</p>
          </details>
          <section className={styles.list} aria-label="Registros de envio">
            <div className={styles.listHeader}>
              <h2>{tabs[applied.view][0]}</h2>
              <strong>{data.pagination.total} registro(s)</strong>
            </div>
            {!data.items.length && (
              <p className={styles.empty}>
                Nenhum trabalho registrado com estes filtros. Boletos do Asaas e
                planilhas aprovadas não são incluídos automaticamente.
              </p>
            )}
            {data.items.map((row) => (
              <article
                className={styles.monitorItem}
                key={row.source + ":" + row.id}
              >
                <div>
                  <span>Fluxo e etapa</span>
                  <strong>{deliverySources[row.source]}</strong>
                  <small>{deliveryStageLabel(row.stage)}</small>
                </div>
                <div>
                  <span>Cliente</span>
                  <strong>
                    {row.billingContext?.customerName ||
                      (row.billingStatus === "LOADING"
                        ? "Consultando cliente…"
                        : "Nome não disponível")}
                  </strong>
                  <small>
                    {row.customerId ||
                      row.billingContext?.customerId ||
                      "Referência não retida"}
                  </small>
                  <span>Cobrança</span>
                  <strong>{row.paymentId || "Vínculo não retido"}</strong>
                </div>
                <div>
                  <span>Valor da cobrança</span>
                  <strong>
                    {row.billingContext
                      ? formatFinancialMoney(row.billingContext.amount)
                      : row.billingStatus === "LOADING"
                        ? "Consultando…"
                        : "Não disponível"}
                  </strong>
                  <span>Parcela</span>
                  <strong>
                    {row.billingStatus === "LOADING"
                      ? "Consultando…"
                      : deliveryInstallmentLabel(row.billingContext)}
                  </strong>
                </div>
                <div>
                  <span>Situação</span>
                  <strong>{deliveryStates[row.state]}</strong>
                  <small>
                    {row.channel === "email" ? "E-mail" : "WhatsApp"}
                  </small>
                </div>
                <div>
                  <span>Programado para</span>
                  <strong>{day(row.scheduledDate)}</strong>
                  <small>Atualizado: {date(row.updatedAt)}</small>
                </div>
                <details className={styles.monitorDetails}>
                  <summary>Ver registro</summary>
                  <dl>
                    <dt>Identificador do trabalho</dt>
                    <dd>{row.id}</dd>
                    <dt>Registrado em (Brasília)</dt>
                    <dd>{date(row.createdAt)}</dd>
                    <dt>Recibo do provedor</dt>
                    <dd>{row.providerMessageId || "Não registrado"}</dd>
                    <dt>Motivo registrado</dt>
                    <dd>{deliveryReasonLabel(row.reasonCode)}</dd>
                    <dt>Dados da cobrança</dt>
                    <dd>
                      {row.billingContext
                        ? `Dados atuais do Asaas, consultados em ${date(row.billingContext.generatedAt)}. O valor corresponde a esta cobrança, não ao total do contrato.`
                        : "Dados atuais não disponíveis. O histórico do envio permanece abaixo."}
                    </dd>
                    <dt>Destinatário usado</dt>
                    <dd>Não retido neste registro histórico</dd>
                  </dl>
                  {row.state === "ACCEPTED" && (
                    <p>Aceitação SMTP não comprova entrega.</p>
                  )}
                  {["UNCERTAIN", "RESERVED", "IN_FLIGHT"].includes(
                    row.state,
                  ) && (
                    <p>
                      Confira o resultado antes de qualquer nova tentativa. Não
                      há reenvio nesta tela.
                    </p>
                  )}
                  {row.state === "CANCELLED" && (
                    <p>
                      Consulte o motivo. Cancelamento não comprova pagamento.
                    </p>
                  )}
                </details>
              </article>
            ))}
          </section>
          <div className={styles.pagination}>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </button>
            <span>
              Página {page} de {Math.max(1, data.pagination.totalPages)}
            </span>
            <button
              type="button"
              disabled={page >= data.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        </>
      )}
    </div>
  );
}
export default withFinancialSession(FinancialDeliveryMonitorPage);
